'use client';

import { Badge } from '@common/components';
import { SvgChevronleft, SvgChevronright, SvgVisible } from '@common/icon';
import { cn } from '@core/utils';
import type { ListPopularPolicyDto } from '@generated/auth';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES } from '@/constants/routes';

import { PolicyAdCard } from './PolicyAdCard';

export interface Policy extends ListPopularPolicyDto {
  badge?: 'peer' | 'neighbor';
}

interface PopularPolicyClientProps {
  policies: Policy[];
  isLogin: boolean;
}

const BADGE_LABEL = {
  peer: '또래',
  neighbor: '이웃',
} as const;

/*
 * 조회수 포맷팅
 * 19800 -> 2만
 * 19000 -> 2만
 * 18900 -> 1만
 * 1000 -> 1000
 * 0 -> 0
 */

const TEN_THOUSAND = 10000;

const formatViewCount = (count: number) => {
  const safeCount = Math.max(0, count);

  if (safeCount < TEN_THOUSAND) {
    return `${safeCount.toLocaleString()}`;
  }

  const t = safeCount / TEN_THOUSAND;

  const roundedTenThousand = Math.floor(t * 10) / 10;
  const integerTenThousand = Math.floor(roundedTenThousand);
  const decimalPart = Math.floor((roundedTenThousand % 1) * 10);

  if (decimalPart >= 9) {
    return `${(integerTenThousand + 1).toLocaleString()}만`;
  }

  return `${integerTenThousand.toLocaleString()}만`;
};

export const PopularPolicyClient = ({
  policies,
  isLogin,
}: PopularPolicyClientProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
  });

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  return (
    <section>
      <div className="flex items-center justify-between px-28 pb-16">
        <div>
          <h2 className="font-700 text-Gray-900 typo-body2">인기 정책</h2>
          <p className="font-500 text-Gray-600 typo-body4">
            {isLogin
              ? '내 프로필 그룹에서 많이 조회된 정책이에요'
              : '지금 전국민이 관심 가지고 있는 정책이에요'}
          </p>
        </div>
        <div className="flex items-center gap-4 mobile-sm:hidden">
          <button
            type="button"
            onClick={scrollPrev}
            className="flex size-24 items-center justify-center rounded-small border border-Gray-200 bg-Gray-white text-Gray-600 transition-colors hover:bg-Gray-25"
            aria-label="이전 슬라이드"
          >
            <SvgChevronleft className="size-16" />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            className="flex size-24 items-center justify-center rounded-small border border-Gray-200 bg-Gray-white text-Gray-600 transition-colors hover:bg-Gray-25"
            aria-label="다음 슬라이드"
          >
            <SvgChevronright className="size-16" />
          </button>
        </div>
      </div>
      <div className="overflow-hidden px-20" ref={emblaRef}>
        <div className="flex">
          {/* 광고 카드 - 첫번째 슬롯 */}
          <PolicyAdCard />
          {policies.map((policy) => (
            <CustomLink
              key={policy.metaPolicyIdIdx}
              href={ROUTES.POLICY_DETAIL.pathname({
                idIdx: policy.metaPolicyIdIdx,
              })}
              className="group mr-8 flex w-220 shrink-0 flex-col justify-between rounded-xlarge bg-Gray-white p-20
              transition-colors
              duration-200
              ease-out hover:bg-Gray-25 focus-visible:outline-none focus-visible:ring-2
              focus-visible:ring-B-500 focus-visible:ring-offset-2 active:scale-[0.98]"
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-4">
                  {policy.badge && (
                    <Badge
                      className={cn('w-fit text-Gray-white', {
                        'bg-M-400': policy.badge === 'peer',
                        'bg-Pink-300': policy.badge === 'neighbor',
                      })}
                    >
                      {BADGE_LABEL[policy.badge]}
                    </Badge>
                  )}
                  {(() => {
                    const ddayText =
                      policy.dday?.replace('마감 ', '') ?? '상시';
                    const ddayMatch = ddayText.match(/D-(\d+)/);
                    const isUrgent = ddayMatch && Number(ddayMatch[1]) <= 7;

                    return (
                      <Badge
                        className={cn('w-fit', {
                          'text-Gray-black': !isUrgent,
                          'text-R-400': isUrgent,
                        })}
                      >
                        {ddayText}
                      </Badge>
                    );
                  })()}
                </div>

                <h3 className="mb-12 line-clamp-2 max-h-21 font-600 text-Gray-900 transition-colors duration-200 typo-body4 group-hover:text-Gray-800 mobile-sm:max-h-fit">
                  {policy.policyName}
                </h3>
              </div>
              <div>
                <span className="line-clamp-1 font-600 text-Gray-550 typo-caption1">
                  {policy.supportBenefit}
                </span>
                <div className="flex items-center gap-2 pt-4 text-B-500">
                  <SvgVisible className="size-12 transition-transform duration-200 group-hover:scale-110" />
                  <span className="font-500 typo-caption1">
                    {formatViewCount(policy.readCount)}명 조회
                  </span>
                </div>
              </div>
            </CustomLink>
          ))}
        </div>
      </div>
    </section>
  );
};
